import useHorizontalGrabScroll from '@utils/hooks/useHorizontalGrabScroll';
import useHorizontalScrollShadows from '@utils/hooks/useHorizontalScrollShadows';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import {
  PropsWithChildren,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';

export interface IShadowContainerHorizontalRef {
  readonly scrollTo: (offset: number) => void;
  readonly getOffsetWidth: () => number;
}

interface Props {
  readonly className?: string;
  readonly shadowColour?: string;
  readonly threshold?: number;
  readonly showStart?: boolean; // Manually override shadows from a parent component
  readonly showEnd?: boolean;
  readonly disableScroll?: boolean; // Used together with showStart/showEnd to disable scrolling within the container (e.g. when we want to wrap a carousel within some left/right shadows, we want to use the carousel's scrolling/swiping, not the container's one)
  readonly shadowWidth?: string;
  readonly isNoWrapEnabled?: boolean;
}

const ShadowContainerHorizontal = forwardRef<
  IShadowContainerHorizontalRef,
  PropsWithChildren<Props>
>(
  (
    {
      children,
      className,
      disableScroll = false,
      shadowColour = 'from-white dark:from-black-100',
      threshold = 0.05,
      showStart = false,
      showEnd = true,
      shadowWidth = 'w-10',
      isNoWrapEnabled = true,
    },
    ref
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const { isStart, isEnd } = useHorizontalScrollShadows(
      containerRef,
      threshold,
      disableScroll
    );
    useHorizontalGrabScroll(containerRef, disableScroll);

    useImperativeHandle(ref, () => ({
      scrollTo: (leftOffset: number) => {
        containerRef.current?.scrollTo({
          left: leftOffset,
          behavior: 'smooth',
        });
      },
      getOffsetWidth: () => containerRef.current?.offsetWidth ?? 0,
    }));

    return (
      <div className={classNames(className, 'relative')}>
        <AnimatePresence>
          {(!isStart || showStart) && (
            <motion.span
              animate={{ opacity: 1 }}
              className={classNames(
                shadowColour,
                shadowWidth,
                'pointer-events-none absolute left-0 top-0 z-10 -ml-[1px] h-full bg-gradient-to-r to-transparent'
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>

        <div
          ref={containerRef}
          // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
          tabIndex={0}
          className={classNames(
            {
              'z-0 overflow-x-hidden': disableScroll,
              'overflow-x-auto': !disableScroll,
              'whitespace-nowrap': isNoWrapEnabled,
            },
            'relative select-none scrollbar-hide'
          )}
        >
          {children}
        </div>

        {/* isEnd is always false when using showEnd override so !isEnd is always true */}
        <AnimatePresence>
          {!isEnd && showEnd && (
            <motion.span
              animate={{ opacity: 1 }}
              className={classNames(
                shadowColour || 'from-white dark:from-black-100',
                shadowWidth,
                'pointer-events-none absolute right-0 top-0 z-10 -ml-[1px] h-full bg-gradient-to-l to-transparent'
              )}
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            />
          )}
        </AnimatePresence>
      </div>
    );
  }
);

export default ShadowContainerHorizontal;
